/**
 * Verfahrensmotor Slice 1 — Portal-Client für n8n **PORTAL_FRIST_APPROVAL** + PB `portal_fristen`.
 * Pattern analog `portalDocuments.ts` (direkter PB-Lesepfad, Schreiben über n8n).
 */

import {
  getPortalPocketBaseUrl,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  PORTAL_FRIST_APPROVAL_URL,
} from '../../../constants';
import {
  demoFristStorageKey,
  isPortalFristApprovalDemoMode,
} from './demoFristSeed';
import type {
  PortalFristApprovalWebhookBody,
  PortalFristRecord,
  PortalFristenApiResult,
  PortalFristStatus,
} from './fristApprovalTypes';
import {
  buildPortalFristManualCardId,
  buildPortalFristenPbFilter,
} from './portalFristenCrud';
import {
  PORTAL_EIGEN_FRIST_REGEL_ID,
  eigenFristKalenderTrace,
  eigenFristTraceScharf,
  planEigenBenannteFrist,
} from './portalEigenFrist';

export { buildPortalFristenPbFilter } from './portalFristenCrud';

export const PORTAL_FRISTEN_COLLECTION = 'portal_fristen';

export const PORTAL_FRISTEN_PB_LIST_SORT = '-id';

export type PortalFristenListResult =
  | { ok: true; data: PortalFristRecord[] }
  | { ok: false; error: string };

/** Leitet den Akte-Ordner aus einem Dokument-NC-Pfad ab (PB `akte_nc_path` ist Ordner, nicht Datei). */
export function deriveAkteNcPathFromDocumentNcPath(
  documentPath?: string | null,
): string | undefined {
  const raw = documentPath?.trim();
  if (!raw) return undefined;

  const normalized = raw.replace(/\\/g, '/').replace(/\/+/g, '/');
  if (normalized.endsWith('/')) return normalized;

  const akteFolder = normalized.match(/^(.+\/(?:Akte|akte)[^/]*)\//i);
  if (akteFolder) return `${akteFolder[1]}/`;

  const lastSlash = normalized.lastIndexOf('/');
  if (lastSlash <= 0) return undefined;
  return `${normalized.slice(0, lastSlash + 1)}`;
}

export function mapPbRecordToPortalFrist(r: Record<string, unknown>): PortalFristRecord | null {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  if (!id) return null;

  const read = (key: string): string =>
    typeof r[key] === 'string' ? (r[key] as string).trim() : '';

  const statusRaw = read('status') as PortalFristStatus;
  const status: PortalFristStatus =
    statusRaw === 'ersetzt' || statusRaw === 'widerrufen' ? statusRaw : 'aktiv';

  return {
    id,
    customer_id: read('customer_id'),
    akte_id: read('akte_id') || undefined,
    kanzlei_id: read('kanzlei_id') || undefined,
    akte_nc_path: read('akte_nc_path'),
    quell_dokument_nc_path: read('quell_dokument_nc_path'),
    frist_typ: read('frist_typ'),
    start_datum: read('start_datum'),
    ende_datum: read('ende_datum'),
    regel_id: read('regel_id'),
    motor_audit_id: read('motor_audit_id'),
    kalender_trace: read('kalender_trace'),
    bestaetigt_von: read('bestaetigt_von'),
    bestaetigt_am: read('bestaetigt_am'),
    status,
    card_id: read('card_id') || undefined,
    created: read('created') || undefined,
    updated: read('updated') || undefined,
  };
}

function normalizePbAuthHeader(authToken: string): string {
  const t = authToken.trim();
  if (!t) return '';
  return t.toLowerCase().startsWith('bearer ') ? t : `Bearer ${t}`;
}

function parseFristApprovalWebhookPayload(data: unknown): Record<string, unknown> {
  const root = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const raw = (root.data ?? root) as Record<string, unknown>;
  const item = Array.isArray(raw) ? raw[0] : raw;
  const nested = (item?.json ?? item ?? root) as Record<string, unknown>;
  if (nested.payload && typeof nested.payload === 'object' && !Array.isArray(nested.payload)) {
    return nested.payload as Record<string, unknown>;
  }
  return nested;
}

function webhookResultFromPayload(payload: Record<string, unknown>): PortalFristenApiResult {
  if (payload.ok === false) {
    const hint = typeof payload.hint === 'string' ? ` (${payload.hint})` : '';
    const detail = typeof payload.detail === 'string' ? ` — ${payload.detail}` : '';
    return {
      ok: false,
      error: String(payload.error ?? 'Webhook-Fehler') + detail + hint,
    };
  }

  const pbRecordId =
    (typeof payload.pbRecordId === 'string' && payload.pbRecordId) ||
    (typeof payload.pb_record_id === 'string' && payload.pb_record_id) ||
    undefined;

  return {
    ok: true,
    pbRecordId,
    motor_audit_id:
      typeof payload.motor_audit_id === 'string' ? payload.motor_audit_id : undefined,
    ende_datum: typeof payload.ende_datum === 'string' ? payload.ende_datum : undefined,
    kalender_trace:
      typeof payload.kalender_trace === 'string' ? payload.kalender_trace : undefined,
    regel_id: typeof payload.regel_id === 'string' ? payload.regel_id : undefined,
  };
}

function loadDemoFristen(customerId: string): PortalFristRecord[] {
  try {
    const raw = localStorage.getItem(demoFristStorageKey(customerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((r) => mapPbRecordToPortalFrist(r as Record<string, unknown>))
      .filter((x): x is PortalFristRecord => Boolean(x) && x.status === 'aktiv');
  } catch {
    return [];
  }
}

function saveDemoFristen(customerId: string, records: PortalFristRecord[]): void {
  try {
    localStorage.setItem(demoFristStorageKey(customerId), JSON.stringify(records));
  } catch {
    /* localStorage voll oder blockiert */
  }
}

function mockFristApprovalWebhook(
  body: PortalFristApprovalWebhookBody,
): PortalFristenApiResult {
  const action = body.action;

  if (action === 'create' || action === 'update') {
    return {
      ok: false,
      error:
        'Fristen-CRUD speichert nur über PocketBase/n8n; der localStorage-Demo-Modus ist dafür gesperrt.',
    };
  }

  if (action === 'reject' || action === 'self_answer') {
    return { ok: true };
  }

  if (action === 'revoke') {
    const pbId = body.pb_record_id?.trim();
    if (!pbId) return { ok: false, error: 'pb_record_id fehlt für Widerruf (Demo).' };
    const items = loadDemoFristen(body.customerId).map((r) =>
      r.id === pbId ? { ...r, status: 'widerrufen' as const } : r,
    );
    saveDemoFristen(body.customerId, items);
    return { ok: true, pbRecordId: pbId };
  }

  if (action !== 'confirm') {
    return { ok: false, error: `Unbekannte action: ${action}` };
  }

  const now = new Date().toISOString();
  const pbId = `demo-pb-${body.card_id}-${Date.now().toString(36)}`;
  const record: PortalFristRecord = {
    id: pbId,
    customer_id: body.customerId,
    akte_nc_path: body.akte_nc_path ?? '',
    quell_dokument_nc_path: body.quell_dokument_nc_path ?? '',
    frist_typ: body.frist_typ_vorschlag ?? 'Berufung (ZPO)',
    start_datum: body.start_datum ?? '2026-07-01',
    ende_datum: '2026-07-15',
    regel_id: body.regel_id ?? 'berufung_zpo_v1',
    motor_audit_id: `demo-audit-${Date.now().toString(36)}`,
    kalender_trace: 'Demo-Mock (ohne Verfahrensmotor).',
    bestaetigt_von: body.bestaetigt_von ?? 'demo@portal.local',
    bestaetigt_am: now,
    status: 'aktiv',
    card_id: body.card_id,
    created: now,
    updated: now,
  };

  const existing = loadDemoFristen(body.customerId).filter((r) => r.id !== pbId);
  saveDemoFristen(body.customerId, [record, ...existing]);

  return {
    ok: true,
    pbRecordId: pbId,
    motor_audit_id: record.motor_audit_id,
    ende_datum: record.ende_datum,
  };
}

async function postFristApprovalWebhook(
  body: PortalFristApprovalWebhookBody,
): Promise<PortalFristenApiResult> {
  if (isPortalFristApprovalDemoMode()) {
    return mockFristApprovalWebhook(body);
  }

  if (!PORTAL_FRIST_APPROVAL_URL) {
    return { ok: false, error: 'Frist-Approval-Webhook nicht konfiguriert.' };
  }

  if (!hasPortalAuthzToken()) {
    return {
      ok: false,
      error:
        'Kein gültiges Portal-Token — bitte neu anmelden.',
    };
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(PORTAL_FRIST_APPROVAL_URL),
    };

    const res = await fetch(PORTAL_FRIST_APPROVAL_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }

    if (!res.ok) {
      const payload = data ? parseFristApprovalWebhookPayload(data) : {};
      const msg =
        typeof payload.error === 'string'
          ? payload.error
          : `Frist konnte nicht gespeichert werden`;
      return { ok: false, error: `${msg} (HTTP ${res.status})` };
    }

    return webhookResultFromPayload(parseFristApprovalWebhookPayload(data));
  } catch {
    return { ok: false, error: 'Verbindung zum Frist-Webhook fehlgeschlagen.' };
  }
}

/** Bestätigung → n8n **PORTAL_FRIST_APPROVAL** → Motor → PB-Index. */
export async function confirmPortalFrist(
  body: Omit<PortalFristApprovalWebhookBody, 'action'>,
): Promise<PortalFristenApiResult> {
  return postFristApprovalWebhook({ ...body, action: 'confirm' });
}

/** Motor-Vorschau (kein PB-Write) — z. B. Demo-Card `?demoFrist=1` mit Labor-Secret. */
export async function previewPortalFrist(
  body: Omit<PortalFristApprovalWebhookBody, 'action'>,
): Promise<PortalFristenApiResult> {
  return postFristApprovalWebhook({ ...body, action: 'preview' });
}

export async function rejectPortalFrist(
  body: Omit<PortalFristApprovalWebhookBody, 'action'>,
): Promise<PortalFristenApiResult> {
  return postFristApprovalWebhook({ ...body, action: 'reject' });
}

export async function selfAnswerPortalFrist(
  body: Omit<PortalFristApprovalWebhookBody, 'action'>,
): Promise<PortalFristenApiResult> {
  return postFristApprovalWebhook({ ...body, action: 'self_answer' });
}

export async function revokePortalFrist(
  body: Omit<PortalFristApprovalWebhookBody, 'action'> & { pb_record_id: string },
): Promise<PortalFristenApiResult> {
  return postFristApprovalWebhook({ ...body, action: 'revoke' });
}

export interface PortalFristManualWriteInput {
  customerId: string;
  akte_id: string;
  akte_nc_path: string;
  frist_typ_vorschlag: string;
  start_datum: string;
  regel_id: string;
  bestaetigt_von?: string;
  tenant?: string;
  customerStatus?: string;
}

/** S2: Frist direkt am sichtbaren Akt anlegen — kein Composer/ARKO-Approval. */
export async function createPortalFristForAkte(
  input: PortalFristManualWriteInput,
): Promise<PortalFristenApiResult> {
  if (!input.customerId.trim()) {
    return { ok: false, error: 'Mandant fehlt — Frist wurde nicht gespeichert.' };
  }
  if (!input.akte_id.trim()) {
    return { ok: false, error: 'akte_id fehlt — Frist wurde nicht gespeichert.' };
  }
  return postFristApprovalWebhook({
    ...input,
    action: 'create',
    card_id: buildPortalFristManualCardId('create', input.akte_id),
    approval_kind: 'frist_vorschlag',
  });
}

/**
 * Eigen benannter Frist-Datensatz in `portal_fristen`.
 * Geht nicht durch den Verfahrensmotor und ersetzt keine Freigabe-Frist.
 */
export async function createEigenBenannteFristForAkte(input: {
  customerId: string;
  akte_id: string;
  akte_nc_path: string;
  name: string;
  datum: string;
  erinnerung?: string;
  bestaetigt_von?: string;
  tenant?: string;
  customerStatus?: string;
}): Promise<PortalFristenApiResult> {
  const plan = planEigenBenannteFrist({
    akteId: input.akte_id,
    name: input.name,
    datum: input.datum,
    erinnerung: input.erinnerung,
  });
  if (plan.ok === false) return { ok: false, error: plan.error };
  if (!input.customerId.trim()) {
    return { ok: false, error: 'Mandant fehlt — Frist wurde nicht gespeichert.' };
  }
  return postFristApprovalWebhook({
    action: 'create',
    customerId: input.customerId.trim(),
    card_id: buildPortalFristManualCardId('create', plan.akteId),
    approval_kind: 'frist_vorschlag',
    akte_id: plan.akteId,
    akte_nc_path: input.akte_nc_path,
    frist_typ_vorschlag: plan.name,
    start_datum: plan.datum,
    erinnerung_datum: plan.erinnerung,
    regel_id: plan.regelId,
    bestaetigt_von: input.bestaetigt_von?.trim() || undefined,
    tenant: input.tenant,
    customerStatus: input.customerStatus,
  });
}

async function patchEigenBenannteFristRecord(
  authToken: string,
  recordId: string,
  akteId: string,
  body: Record<string, string>,
): Promise<PortalFristenApiResult> {
  const id = recordId.trim();
  const aid = akteId.trim();
  if (!id || !aid) {
    return { ok: false, error: 'Frist gehört nicht zu dieser Akte — Änderung verweigert.' };
  }
  const token = normalizePbAuthHeader(authToken);
  if (!token) {
    return { ok: false, error: 'Fristen konnten ohne gültige Portal-Sitzung nicht geändert werden.' };
  }
  const pbUrl = getPortalPocketBaseUrl().replace(/\/$/, '');
  const recordUrl = `${pbUrl}/api/collections/${PORTAL_FRISTEN_COLLECTION}/records/${id}`;
  try {
    const currentRes = await fetch(recordUrl, { headers: { Authorization: token } });
    if (!currentRes.ok) {
      return { ok: false, error: 'Frist gehört nicht zu dieser Akte — Änderung verweigert.' };
    }
    const current = mapPbRecordToPortalFrist((await currentRes.json()) as Record<string, unknown>);
    const eigenOnly = 'frist_typ' in body || 'start_datum' in body || 'ende_datum' in body;
    if (!current || current.akte_id !== aid) {
      return { ok: false, error: 'Frist gehört nicht zu dieser Akte — Änderung verweigert.' };
    }
    if (eigenOnly && current.regel_id !== PORTAL_EIGEN_FRIST_REGEL_ID) {
      return { ok: false, error: 'Frist gehört nicht zu dieser Akte — Änderung verweigert.' };
    }
    const res = await fetch(recordUrl, {
      method: 'PATCH',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      return { ok: false, error: `Frist konnte nicht geändert werden (PocketBase HTTP ${res.status}).` };
    }
    const mapped = mapPbRecordToPortalFrist((await res.json()) as Record<string, unknown>);
    if (!mapped || mapped.id !== id || mapped.akte_id !== aid || mapped.regel_id !== current.regel_id) {
      return { ok: false, error: 'Frist gehört nicht zu dieser Akte — Änderung verweigert.' };
    }
    return { ok: true, pbRecordId: mapped.id };
  } catch {
    return { ok: false, error: 'Eigene Frist konnte nicht geändert werden.' };
  }
}

/** Name, Datum, Erinnerung und scharf nur an der eigenen Frist dieser Akte. */
export async function updateEigenBenannteFristForAkte(input: {
  authToken: string;
  recordId: string;
  akteId: string;
  name: string;
  datum: string;
  erinnerung?: string;
  scharf: boolean;
}): Promise<PortalFristenApiResult> {
  const plan = planEigenBenannteFrist({
    akteId: input.akteId,
    name: input.name,
    datum: input.datum,
    erinnerung: input.erinnerung,
  });
  if (plan.ok === false) return { ok: false, error: plan.error };
  return patchEigenBenannteFristRecord(input.authToken, input.recordId, plan.akteId, {
    frist_typ: plan.name,
    start_datum: plan.datum,
    ende_datum: plan.endeDatum,
    kalender_trace: eigenFristKalenderTrace(plan.datum, plan.endeDatum, input.scharf),
  });
}

/** Haken an der Frist dieser Akte. Die gespeicherte Spur bleibt, nur das Zeichen davor wechselt. */
export async function setEigenBenannteFristScharf(input: {
  authToken: string;
  recordId: string;
  akteId: string;
  trace?: string;
  scharf: boolean;
}): Promise<PortalFristenApiResult> {
  return patchEigenBenannteFristRecord(input.authToken, input.recordId, input.akteId, {
    kalender_trace: eigenFristTraceScharf(input.trace, input.scharf),
  });
}

/** Nimmt die eigene Frist aus der aktiven Liste dieser Akte. */
export async function deleteEigenBenannteFristForAkte(input: {
  authToken: string;
  recordId: string;
  akteId: string;
}): Promise<PortalFristenApiResult> {
  return patchEigenBenannteFristRecord(input.authToken, input.recordId, input.akteId, {
    status: 'widerrufen',
  });
}

/** S2: Bestehende aktive Frist im selben Akt neu berechnen und aktualisieren. */
export async function updatePortalFristForAkte(
  input: PortalFristManualWriteInput & { pb_record_id: string },
): Promise<PortalFristenApiResult> {
  if (!input.customerId.trim()) {
    return { ok: false, error: 'Mandant fehlt — Frist wurde nicht gespeichert.' };
  }
  if (!input.akte_id.trim()) {
    return { ok: false, error: 'akte_id fehlt — Frist wurde nicht gespeichert.' };
  }
  if (!input.pb_record_id.trim()) {
    return { ok: false, error: 'Fristen-ID fehlt — Änderung wurde nicht gespeichert.' };
  }
  return postFristApprovalWebhook({
    ...input,
    action: 'update',
    card_id: buildPortalFristManualCardId('update', input.akte_id),
    approval_kind: 'frist_vorschlag',
  });
}

/**
 * Aktive Fristen einer Akte aus PB `portal_fristen` (Mandanten-Token).
 * Demo: `?demoFrist=1` ohne Secret → localStorage-Mock nach Bestätigung.
 */
export async function listPortalFristenForAkteResult(
  customerId: string,
  authToken: string,
  akteNcPath?: string,
  akteId?: string,
): Promise<PortalFristenListResult> {
  if (isPortalFristApprovalDemoMode()) {
    const all = loadDemoFristen(customerId);
    if (!akteNcPath?.trim()) return { ok: true, data: all };
    const needle = akteNcPath.trim().replace(/\/+$/, '').toLowerCase();
    return {
      ok: true,
      data: all.filter(
        (r) => r.akte_nc_path.replace(/\/+$/, '').toLowerCase() === needle,
      ),
    };
  }

  const token = normalizePbAuthHeader(authToken);
  if (!token) {
    return { ok: false, error: 'Fristen konnten ohne gültige Portal-Sitzung nicht geladen werden.' };
  }

  const pbUrl = getPortalPocketBaseUrl().replace(/\/$/, '');
  const filter = encodeURIComponent(
    buildPortalFristenPbFilter(customerId, akteNcPath, akteId),
  );
  const sort = encodeURIComponent(PORTAL_FRISTEN_PB_LIST_SORT);

  try {
    const res = await fetch(
      `${pbUrl}/api/collections/${PORTAL_FRISTEN_COLLECTION}/records?filter=${filter}&sort=${sort}&perPage=50`,
      {
        headers: { Authorization: token },
      },
    );
    if (!res.ok) {
      return {
        ok: false,
        error: `Fristen konnten nicht neu geladen werden (PocketBase HTTP ${res.status}).`,
      };
    }
    const data = (await res.json()) as { items?: Record<string, unknown>[] };
    return {
      ok: true,
      data: (data.items ?? [])
        .map(mapPbRecordToPortalFrist)
        .filter((x): x is PortalFristRecord => Boolean(x)),
    };
  } catch {
    return { ok: false, error: 'Verbindung zur Fristen-Liste fehlgeschlagen.' };
  }
}

/** Read-only-Kompatibilitaet (z. B. Kalender); der Akt-Hub nutzt das strikte Resultat. */
export async function listPortalFristenForAkte(
  customerId: string,
  authToken: string,
  akteNcPath?: string,
  akteId?: string,
): Promise<PortalFristRecord[]> {
  const result = await listPortalFristenForAkteResult(
    customerId,
    authToken,
    akteNcPath,
    akteId,
  );
  return result.ok ? result.data : [];
}
